import { MouseEventHandler } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import Link from '@/atoms/Link/Link'

/* utils */
import { dataLayerPostClickEvent } from '@/utils/functions/dataLayerEvents'

/* assets */
import { ReactComponent as QuoteIcon } from 'public/assets/icons/quote.svg'
import { ReactComponent as ArrowIcon } from 'public/assets/icons/chevron-right.svg'

interface CaseStudyCardProps {
  className?: string
  data: CaseStudy
  isFeatured?: boolean
  isInverted?: boolean
  preventRedirection?: boolean
}

interface CardProps extends CaseStudyCardProps {
  onClick: MouseEventHandler<HTMLAnchorElement>
}

function FeaturedCard(props: CardProps) {
  return (
    <Link
      href={`/case-study/${props.data.slug}/`}
      className={clsx(
        'group  |  grid md:grid-cols-12 gap-8 items-center  |  py-8  |  focus:outline-none',
        props.preventRedirection && 'cursor-default',
        props.className
      )}
      onClick={props.onClick}
    >
      <div
        className={clsx(
          'lg:px-10',
          props.isInverted
            ? 'md:col-start-7 md:col-end-13 lg:col-start-7 lg:col-end-12 md:order-2'
            : 'md:col-start-1 md:col-end-7 lg:col-start-2 lg:col-end-7 md:order-1'
        )}
      >
        <div className="mb-9">
          {props.data.caseStudy.customerLogo?.sourceUrl && (
            <Image
              src={props.data.caseStudy.customerLogo?.sourceUrl}
              alt={props.data.caseStudy.customer}
              height="40"
              width="150"
              loading="lazy"
              className="mb-6"
            />
          )}
          <h2 className="text-paragraph-2 lg:text-paragraph-1 font-medium  |  mb-3">
            {props.data.title}
          </h2>
          {!props.preventRedirection && (
            <p className="text-paragraph-4 font-medium text-blue-40  |  flex items-center gap-2 group-focus:underline group-focus:text-blue-50 group-hover:underline group-hover:text-blue-50">
              Read Case Study
              <ArrowIcon />
            </p>
          )}
        </div>

        {props.data.caseStudy.testimonial.content && (
          <div>
            <QuoteIcon className="text-blue-50  |  mb-4" />

            <blockquote className="text-paragraph-1 text-gray-70  |  mb-8">
              {props.data.caseStudy.testimonial.content}
            </blockquote>
            <cite className="flex items-center gap-6  |  not-italic">
              <img
                src={props.data.caseStudy.testimonial.authorImage?.sourceUrl}
                alt={props.data.caseStudy.testimonial.authorName}
                className="rounded-full"
                width="48"
                height="48"
                loading="lazy"
              />
              <div>
                <p className="text-paragraph-4 font-medium  |  mb-1">
                  {props.data.caseStudy.testimonial.authorName}
                </p>
                <p className="text-paragraph-4 text-gray-70">
                  {props.data.caseStudy.testimonial.authorInformation}
                </p>
              </div>
            </cite>
          </div>
        )}
      </div>

      <div
        className={clsx(
          props.isInverted
            ? 'md:col-start-1 md:col-end-7 lg:col-start-2 lg:col-end-7 md:order-1'
            : 'md:col-start-7 md:col-end-13 lg:col-start-7 lg:col-end-12 md:order-2'
        )}
      >
        <div className="max-w-[430px] max-h-[430px] overflow-hidden  | rounded-lg  |  mx-auto">
          {props.data.featuredImage && (
            <Image
              src={props.data.featuredImage.node.sourceUrl}
              alt={props.data.caseStudy.customer}
              width="430"
              height="430"
              loading="lazy"
              className="w-full h-auto  |  transition-transform ease-linear  |  group-hover:scale-110"
            />
          )}
        </div>
      </div>
    </Link>
  )
}

function NormalCard(props: CardProps) {
  return (
    <Link
      href={`/case-study/${props.data.slug}/`}
      className={clsx(
        'group  |  bg-white  |  flex flex-col items-start  |  border border-gray-20 rounded-lg shadow-small  |  p-8  |  focus-ring',
        props.className
      )}
      onClick={props.onClick}
    >
      {props.data.caseStudy.customerLogo && (
        <Image
          src={props.data.caseStudy.customerLogo.sourceUrl}
          alt={props.data.caseStudy.customer}
          height="32"
          width={props.data.caseStudy.customerLogo.mediaDetails?.width}
          loading="lazy"
          className="max-h-8 mb-6 w-auto"
        />
      )}
      <h2 className="text-paragraph-2 font-medium  |  line-clamp-3 h-22  |  mb-8">
        {props.data.title}
      </h2>
      <p className="text-paragraph-4 font-medium text-blue-40  |  flex items-center gap-2  |  mt-auto  |  group-focus:underline group-focus:text-blue-50 group-hover:underline group-hover:text-blue-50">
        Read Case Study
        <ArrowIcon />
      </p>
    </Link>
  )
}

function CaseStudyCard(props: CaseStudyCardProps) {
  const onClick: MouseEventHandler<HTMLAnchorElement> = e => {
    if (props.preventRedirection) {
      e.preventDefault()
      return
    }

    dataLayerPostClickEvent({
      title: props.data.title,
      type: 'case-study',
      url: `/case-study/${props.data.slug}`
    })
  }

  if (props.isFeatured) return <FeaturedCard onClick={onClick} {...props} />

  return <NormalCard onClick={onClick} {...props} />
}

export default CaseStudyCard
